import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from 'react-router-dom';
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
import { postApiCall } from '../../utils/Apicalls';
import { generateToken } from "../../utils/tokenGenerate";
import { encryptData } from "../../utils/simpleCrypto";
import env from '../../utils/env';
import "../home/home.css";

function InstallationScreen() {
  const contactRef = useRef(null);
  const [generateTokenShow, setGenerateTokenShow] = useState(false);
  const [copyToken, setCopyToken] = useState(false);
  const [tokenDetails, setTokenDetails] = useState({
    token: null,
    jobTitle: null,
    skillset: null,
  });
  const [userDetails, setUserDetails] = useState({
    id: "",
    name: "",
    email: "",
    takeDesktopDemo: true,
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let userData = localStorage.getItem("userDetails");
    if (!userDetails) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    userData = JSON.parse(userData);
    let name = userData?.first_name || "";
    if (userData?.last_name) {
      name += " " + userData?.last_name;
    }
    let takeDesktopDemo = true;
    if (userData?.take_desktop_demo === "0") {
      takeDesktopDemo = false;
    }
    setUserDetails({
      id: userData?.id || "",
      name: name,
      email: userData?.email || "",
      takeDesktopDemo: takeDesktopDemo,
    });
  }, []);

  const generateInterviewToken = async (details) => {
    let error = false;
    setErrors({});
    if (!details.jobTitle) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        jobTitle: "Job title is required to generate a token.",
      }));
      error = true;
    }

    if (!details.skillset) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        skillset: "Skillset is required to generate a token.",
      }));
      error = true;
    }

    if (!error) {
      console.log("Generating token with details:", details);

      const tokenParams = {
        userId: userDetails?.id || "",
        name: userDetails?.name || "",
        email: userDetails?.email || "",
        resume: "",
        skills: [tokenDetails.skillset],
        jobDescription: "",
        sessionStart: new Date().toISOString(),
        sessionDurationMinutes: 15,
        OpenApiKey: env.OPENAI_API_KEY,
        AzureApiKey: env.AZURE_API_KEY
      };
      const encryptOpenApiKey = await encryptData(env.OPENAI_API_KEY, env.DESKTOP_API_SECRET);
      if (encryptOpenApiKey) {
        tokenParams.OpenApiKey = encryptOpenApiKey;
      }
      const encryptAzureApiKey = await encryptData(env.AZURE_API_KEY, env.DESKTOP_API_SECRET);
      if (encryptAzureApiKey) {
        tokenParams.AzureApiKey = encryptAzureApiKey;
      }
      const desktopToken = await generateToken(tokenParams, "15m");
      if (desktopToken) {
        try {
          const result = await postApiCall('interview-app/users/update', {
            takeDesktopDemo: "1",
          });
          
          if (result?.status) {
            setTokenDetails({
              ...tokenDetails,
              token: desktopToken,
            });

            setUserDetails({
              ...userDetails,
              takeDesktopDemo: true,
            });

            let userData = localStorage.getItem("userDetails");
            if (userData) {
              userData = JSON.parse(userData);
              userData.take_desktop_demo = "1";
              localStorage.setItem("userDetails", JSON.stringify(userData));
            }
          }
        } catch (err) {
          console.error("Error fetching plans:", err);
        }
      }
    }
  };

  return (
    <>
      <div className="home-page">
        <Header />
        <main className="home-content">
          <section className="how-it-works-section">
            <div className="container">
              <h2>Installation Instructions</h2>
              <p className="section-subtitle">
                Get installation-ready in 3 simple steps
              </p>

              <div className="steps-container">
                <div className="step-card">
                  <div className="step-content">
                    <div className="step-number">1</div>
                    <h3>Download</h3>
                    <p>
                      Click download button
                    </p>
                  </div>
                  <div className="step-visual">
                    <div className="text-center">
                      <Link to={env.API_ASSETS_URL + "WindowsService.7z"} target="_blank" className="mini-button" style={{width: "100%", fontSize: 15, textDecoration: "none"}}>Download</Link>
                    </div>
                  </div>
                </div>

                <div className="step-card">
                  <div className="step-content">
                    <div className="step-number">2</div>
                    <h3>Extract</h3>
                    <p> 
                      Unzip the file
                    </p>
                  </div>
                </div>

                <div className="step-card">
                  <div className="step-content">
                    <div className="step-number">3</div>
                    <h3>Run</h3>
                    <p>
                      Double-click the Windows.exe file
                    </p>
                  </div>
                </div>
              </div>

              <div className="demo-cta">
                <div className="demo-conversation-token">
                  <div className="demo-interviewer">
                    <div className="row text-start">
                      <div className="col-12">
                        <h5>💻 System Requirements:</h5>
                      </div>
                      <div className="col-12">
                        <ul style={{margin: "10px 0", padding: "0 0 0 20px"}}>
                          <li>Windows</li>
                          <li>2GB RAM minimum (8GB recommended)</li>
                          <li>300MB free disk space</li>
                          <li>Internet connection for AI features</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="demo-cta">
                {!userDetails?.takeDesktopDemo && userDetails?.id && (
                  <button
                    className="demo-button"
                    onClick={() => {
                      setGenerateTokenShow(true);
                      setTokenDetails({
                        token: null,
                        expiry: null,
                        jobTitle: null,
                        skillset: null,
                      });
                    }}
                  >
                    Try Free Demo
                  </button>
                )}
                {generateTokenShow && (
                  <div className="demo-conversation-token">
                    <div className="demo-interviewer">
                      <div className="row text-start">
                        {tokenDetails.token && (
                          <>
                            <div className="col-12">
                              <h5>Generated Token</h5>
                            </div>
                            <div className="col pt-3">
                              <strong>Token:</strong>
                            </div>
                            <div
                              className="col-2"
                              style={{ textAlign: "right" }}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    tokenDetails.token
                                  );
                                  setCopyToken(true);
                                  setTimeout(() => setCopyToken(false), 5000);
                                }}
                                className="copy-button"
                              >
                                {copyToken && (
                                  <>
                                    <CheckIcon style={{ marginRight: 4 }} />{" "}
                                    Copyd!
                                  </>
                                )}
                                {!copyToken && (
                                  <>
                                    <ContentCopyIcon
                                      style={{ marginRight: 4 }}
                                    />{" "}
                                    Copy
                                  </>
                                )}
                              </Button>
                            </div>
                            <div className="col-12 mt-2">
                              <textarea
                                id="tokenDetails"
                                value={tokenDetails.token}
                                placeholder=""
                                rows={14}
                                className="form-control"
                              />
                            </div>
                          </>
                        )}
                        {!tokenDetails.token && (
                          <>
                            <div className="col-8 mb-3">
                              <h5>Generate Token</h5>
                            </div>
                            <div className="col-4 text-end">
                              <button
                                className="btn"
                                onClick={() => setGenerateTokenShow(false)}
                              >
                                <CloseIcon />
                              </button>
                            </div>
                            <div className="col-12 mb-3">
                              <label htmlFor="jobTitle">Job Title*</label>
                              <input
                                type="text"
                                id="jobTitle"
                                value={tokenDetails.jobTitle || ""}
                                onChange={(e) =>
                                  setTokenDetails({
                                    ...tokenDetails,
                                    jobTitle: e.target.value,
                                  })
                                }
                                placeholder="e.g. Frontend Developer"
                                className={
                                  errors?.jobTitle
                                    ? "form-control is-invalid"
                                    : "form-control"
                                }
                              />
                              {errors?.jobTitle && (
                                <p className="error-text">{errors.jobTitle}</p>
                              )}
                            </div>
                            <div className="col-12">
                              <label htmlFor="skillset">Your Skillset*</label>
                              <input
                                type="text"
                                id="skillset"
                                value={tokenDetails.skillset || ""}
                                onChange={(e) =>
                                  setTokenDetails({
                                    ...tokenDetails,
                                    skillset: e.target.value,
                                  })
                                }
                                placeholder="Input skills separated by commas (e.g. React, JavaScript, CSS)"
                                className={
                                  errors?.skillset
                                    ? "form-control is-invalid"
                                    : "form-control"
                                }
                              />
                              {errors?.skillset && (
                                <p className="error-text">{errors.skillset}</p>
                              )}
                              <p className="help-text text-start">
                                Enter your skills separated by commas
                              </p>
                            </div>
                            <div className="col-12">
                              <button
                                type="submit"
                                className="start-button"
                                onClick={() =>
                                  generateInterviewToken(tokenDetails)
                                }
                              >
                                Generate Token
                              </button>
                              <p className="help-text text-start">
                                * Demo token is valid for 15 minutes from the time of generation
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="demo-interface-preview">
                <h2 style={{ textAlign: "center" }}>Interview AI Assistant</h2>
                <p style={{ textAlign: "center", marginBottom: 20 }}>
                    Powerful desktop application with real-time AI assistance for your interviews
                </p>
                <div className="row">
                  <div className="col-3 border-end">
                    <h5 className="text-center">🎧 Real-time Audio</h5>
                    <p className="text-center">Speech recognition and live transcription</p>
                  </div>
                  <div className="col-3 border-end">
                    <h5 className="text-center">💬 AI Assistance</h5>
                    <p className="text-center">GPT-4 powered interview guidance</p>
                  </div>
                  <div className="col-3 border-end">
                    <h5 className="text-center">📸 Screen Analysis</h5>
                    <p className="text-center">Coding challenge problem solving</p>
                  </div>
                  <div className="col-3">
                    <h5 className="text-center">🔒 Privacy Protected</h5>
                    <p className="text-center">Hidden from screen sharing</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
export default InstallationScreen;
